import { useEffect, useRef } from "react";
import { SCRUB_FIELD_LABEL_CLASS } from "./scrubFieldStyles";

interface NumericScrubLabelProps {
  label: string;
  value: number | undefined;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  onChange: (value: number) => void;
  onScrubStart?: () => void;
  onScrubEnd?: () => void;
}

export function NumericScrubLabel({
  label,
  value,
  min,
  max,
  step = 1,
  disabled = false,
  onChange,
  onScrubStart,
  onScrubEnd,
}: NumericScrubLabelProps) {
  const onChangeRef = useRef(onChange);
  const onScrubStartRef = useRef(onScrubStart);
  const onScrubEndRef = useRef(onScrubEnd);
  const cleanupRef = useRef<(() => void) | undefined>(undefined);

  onChangeRef.current = onChange;
  onScrubStartRef.current = onScrubStart;
  onScrubEndRef.current = onScrubEnd;

  useEffect(() => () => cleanupRef.current?.(), []);

  function handlePointerDown(event: React.PointerEvent<HTMLSpanElement>): void {
    if (disabled || event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    cleanupRef.current?.();

    const target = event.currentTarget;
    target.setPointerCapture(event.pointerId);

    const startValue = Number.isFinite(value) ? value! : 0;
    const startX = event.clientX;
    let lastValue = startValue;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
    onScrubStartRef.current?.();

    function handlePointerMove(moveEvent: PointerEvent): void {
      moveEvent.preventDefault();
      const dx = moveEvent.clientX - startX;
      const next = clampValue(roundForStep(startValue + dx * step, step), min, max);
      if (next === lastValue) {
        return;
      }

      lastValue = next;
      onChangeRef.current(next);
    }

    function handlePointerEnd(endEvent: PointerEvent): void {
      if (target.hasPointerCapture(endEvent.pointerId)) {
        target.releasePointerCapture(endEvent.pointerId);
      }
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerEnd);
      window.removeEventListener("pointercancel", handlePointerEnd);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      cleanupRef.current = undefined;
      onScrubEndRef.current?.();
    }

    cleanupRef.current = () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerEnd);
      window.removeEventListener("pointercancel", handlePointerEnd);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      onScrubEndRef.current?.();
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerEnd);
    window.addEventListener("pointercancel", handlePointerEnd);
  }

  return (
    <span
      className={`${SCRUB_FIELD_LABEL_CLASS} ${disabled ? "" : "cursor-ew-resize touch-none"}`}
      onPointerDown={handlePointerDown}
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      {label}
    </span>
  );
}

function clampValue(value: number, min: number | undefined, max: number | undefined): number {
  const withMin = min === undefined ? value : Math.max(min, value);
  return max === undefined ? withMin : Math.min(max, withMin);
}

function roundForStep(value: number, step: number): number {
  const precision = decimalPrecision(step);
  const multiplier = 10 ** precision;
  return Math.round(value * multiplier) / multiplier;
}

function decimalPrecision(value: number): number {
  const [, fraction = ""] = String(value).split(".");
  return fraction.length;
}
